/**
 * i18nStrict — Dev-time guardrail against English bleed in FR/ES locales.
 *
 * Logs a console warning every time a visible DOM text node contains
 * an English-only phrase while i18n.language is FR or ES.
 *
 * Cost: zero in production builds (short-circuits on NODE_ENV).
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { detectEnglishBleed } from './i18nBleedHeuristic';

const seen = new Set();

const isVisible = (el) => {
    if (!el || !(el instanceof Element)) return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    if (parseFloat(style.opacity) === 0) return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
};

const flag = (node, run) => {
    const el = node.parentElement;
    if (!el || !isVisible(el)) return;
    const sig = `${el.tagName}:${run}`;
    if (seen.has(sig)) return;
    seen.add(sig);
    // eslint-disable-next-line no-console
    console.warn(
        `[i18nStrict] Possible untranslated English: "${run}"`,
        '\n  element:', el,
        '\n  hint: wrap with t(\'...\') and add the key to en.json/fr.json/es.json'
    );
};

const scanNode = (root) => {
    if (!root || !root.nodeType) return;
    // For element nodes, gather aggregated text per common block element.
    // This avoids splitting a single phrase across multiple text nodes
    // (which is what produces false negatives).
    const blocks = root.querySelectorAll
        ? root.querySelectorAll('p,span,h1,h2,h3,h4,h5,h6,li,td,th,button,label,a,small,strong')
        : [];
    for (const el of blocks) {
        if (!isVisible(el)) continue;
        if (el.children.length > 0) continue; // Leaf-ish only
        const txt = (el.innerText || el.textContent || '').trim();
        const run = detectEnglishBleed(txt);
        if (run) {
            const sig = `${el.tagName}:${run}`;
            if (seen.has(sig)) continue;
            seen.add(sig);
            // eslint-disable-next-line no-console
            console.warn(
                `[i18nStrict] Possible untranslated English: "${run}"`,
                '\n  element:', el,
                '\n  hint: wrap with t(\'...\') and add the key to en.json/fr.json/es.json'
            );
        }
    }
};

export function useStrictI18n() {
    const { i18n } = useTranslation();

    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return;
        const lang = (i18n.language || 'en').slice(0, 2);
        if (lang === 'en') return;

        seen.clear();

        let raf = requestAnimationFrame(() => scanNode(document.body));

        const observer = new MutationObserver((mutations) => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                for (const m of mutations) {
                    m.addedNodes.forEach((n) => {
                        if (n.nodeType === Node.ELEMENT_NODE) scanNode(n);
                    });
                }
            });
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            characterData: true,
        });

        // eslint-disable-next-line no-console
        console.info(`[i18nStrict] Watching for untranslated English in "${lang}" mode.`);

        return () => {
            cancelAnimationFrame(raf);
            observer.disconnect();
        };
    }, [i18n.language]);
}

export default useStrictI18n;
